'use client';

import classNames from '@sindresorhus/class-names';
import {SwitchLanguage} from '@ui-kit/src';
import {Header} from '@ui-kit/src/Header';
import Link from 'next/link';
import {usePathname} from 'next/navigation';
import React, {useEffect, useMemo, useState} from 'react';

import {
  onLinkClickProps,
  StrapiHeaderProps,
} from '@dev-landing/components/StrapiHeader/StrapiHeader.types';

import {Languages} from '@dev-landing/types/global';

import {useButtonClick} from '@dev-landing/analytics/hooks/useButtonClick';
import {useRustoreAppears} from '@dev-landing/analytics/hooks/useRustoreAppears';

import {logTmrEvent, logYmEvent} from '@dev-landing/analytics/analytics';
import {EVENTS, YM_EVENTS} from '@dev-landing/analytics/events';

import styles from './Header.module.scss';

export const RightSideLink = ({
  isTopScroll,
  href,
  text,
  showLanguageSwitcher,
  locale,
}: {
  isTopScroll: boolean;
  href: string;
  text: string;
  showLanguageSwitcher?: boolean;
  locale: Languages;
}) => {
  const {logButtonClick} = useButtonClick();
  const mobileButtonText = useMemo(() => {
    switch (locale) {
      case 'ru':
        return 'Создать';
      case 'en':
        return 'Sign Up';
      case 'zh':
        return '创建账户';

      default:
        return 'Создать';
    }
  }, [locale]);
  const handleLinkClick = () => {
    logTmrEvent({
      logEvent: EVENTS.headerRegisterButton,
      params: {
        button_text: text,
      },
    });
    logButtonClick(text);
  };
  return (
    <div className={styles.wrapper}>
      {showLanguageSwitcher && <SwitchLanguage />}
      <Link
        href={href}
        className={classNames(styles.link, {
          [styles.isScroll]: !isTopScroll,
        })}
        onClick={handleLinkClick}
      >
        <p className={styles.regular}>{text}</p>
        <p className={styles.smallMobile}>{mobileButtonText}</p>
      </Link>
    </div>
  );
};

export default function HeaderComponent({
  links,
  logo,
  mobileMenu,
  anchorLinks,
  toolsAnchorsLink,
  activeLinkOrder,
  consoleLinkHref,
  consoleLinkText,
  locale,
  showLanguageSwitcher,
}: StrapiHeaderProps) {
  const pathName = usePathname();
  const [activeAnchor, setActiveAnchor] = useState('');
  const {logRustoreAppears} = useRustoreAppears(locale);
  const isBlog = pathName?.includes('/blog');
  const showLocalization = showLanguageSwitcher && !isBlog;
  const isMainPage =
    pathName === '/' || pathName === '/en' || pathName === '/zh';
  const isToolsPage =
    pathName === '/tools' ||
    pathName === '/en/tools' ||
    pathName === '/zh/tools';

  const anchors = useMemo(() => {
    const anchors = isToolsPage ? toolsAnchorsLink : anchorLinks;

    return anchors?.map((link) => ({
      ...link,
      isActiveAnchor: link.href === activeAnchor,
    }));
  }, [activeAnchor, anchorLinks, pathName]);

  const onLinkClick = (props: onLinkClickProps) => {
    switch (props.title) {
      case 'Разработчикам':
        logYmEvent(YM_EVENTS.HEADER_MENU_BUTTON_DEVELOPERS);

        logTmrEvent({logEvent: EVENTS.headerLinkClick_devs});

        break;

      case 'Игры':
        logYmEvent(YM_EVENTS.HEADER_MENU_BUTTON_GAMES);

        logTmrEvent({logEvent: EVENTS.headerLinkClick_games});

        break;

      case 'Помощь':
        logYmEvent(YM_EVENTS.HEADER_MENU_BUTTON_HELP);

        logTmrEvent({logEvent: EVENTS.headerLinkClick_help});

        break;

      case 'Приложения':
        logYmEvent(YM_EVENTS.HEADER_MENU_BUTTON_APPS);

        logTmrEvent({logEvent: EVENTS.headerLinkClick_apps});

        break;
    }

    logTmrEvent({
      logEvent: EVENTS.headerLinkClick,
      params: {
        button_text: props.title,
      },
    });
  };

  const onAnchorClick = (props: onLinkClickProps) => {
    logTmrEvent({
      logEvent: EVENTS.headerAnchorClick,
      params: {
        button_text: props.title,
      },
    });
  };

  useEffect(() => {
    logRustoreAppears();
  }, []);

  return (
    <Header
      mobileMenu={{mobileMenuTitle: mobileMenu.mobileMenuTitle}}
      links={links}
      onAnchorLinkClick={({href, ...link}) => {
        setActiveAnchor(href || '');
        onAnchorClick({href, ...link});
      }}
      logo={logo}
      activeLinkOrder={activeLinkOrder}
      anchorLinks={isMainPage || isToolsPage ? anchors : []}
      onLinkClick={onLinkClick}
      rightSideContent={({isTopScroll}) =>
        RightSideLink({
          isTopScroll,
          href: consoleLinkHref,
          text: consoleLinkText,
          showLanguageSwitcher: showLocalization,
          locale,
        })
      }
    />
  );
}
